import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  RealtimeLayer as TrackerLayer,
  realtimeConfig
} from "mobility-toolbox-js/ol";
import { getHoursAndMinutes, getDelayString } from "../../utils/timeUtils";
import ReactTransitPropTypes from "../../propTypes";
import firstStation from "../../images/RouteSchedule/firstStation.png";
import station from "../../images/RouteSchedule/station.png";
import lastStation from "../../images/RouteSchedule/lastStation.png";
import line from "../../images/RouteSchedule/line.png";
const { getBgColor } = realtimeConfig;
const getDelayColor = (time) => {
  const secs = Math.round(time / 1800 / 2 * 3600 / 1e3);
  if (secs >= 3600) {
    return "dark-red";
  }
  if (secs >= 500) {
    return "middle-red";
  }
  if (secs >= 300) {
    return "light-red";
  }
  if (secs >= 180) {
    return "orange";
  }
  return "green";
};
const isNotStop = (stop) => {
  return !stop.arrivalTime && !stop.departureTime;
};
const isPassed = (stop, time, stops, idx) => {
  if (isNotStop(stop)) {
    if (stops[idx - 1] && idx > 0) {
      return isPassed(stops[idx - 1], time, stops, idx);
    }
    return true;
  }
  const timeToCompare = stop.aimedDepartureTime || stop.aimedArrivalTime || 0;
  let delayToCompare = stop.departureDelay || stop.arrivalDelay || 0;
  if (delayToCompare < 0) {
    delayToCompare = 0;
  }
  return timeToCompare + delayToCompare <= time;
};
const defaultRenderStationImg = (stations, index, isStationPassed, isNotStation) => {
  const { length } = stations;
  let src = station.src || station;
  if (index === 0) {
    src = firstStation.src || firstStation;
  } else if (index === length - 1) {
    src = lastStation.src || lastStation;
  } else if (isNotStation) {
    src = line.src || line;
  }
  return /* @__PURE__ */ React.createElement("img", { src, alt: "routeScheduleLine", className: "rt-route-icon" });
};
const defaultRenderStationName = (stations, index, cancelled) => {
  const { stationName } = stations[index];
  return /* @__PURE__ */ React.createElement("div", { className: cancelled ? "rt-route-cancelled" : "" }, stationName);
};
function RouteStop({
  lineInfos,
  onStationClick,
  trackerLayer,
  renderStationImg,
  renderStationName,
  stop,
  idx
}) {
  const {
    arrivalDelay,
    departureDelay,
    state,
    aimedArrivalTime,
    aimedDepartureTime
  } = stop;
  const cancelled = state === "JOURNEY_CANCELLED" || state === "STOP_CANCELLED";
  const { stations } = lineInfos;
  const isFirstStation = idx === 0;
  const isLastStation = idx === stations.length - 1;
  const isNotStation = isNotStop(stop);
  const [isStationPassed, setIsStationPassed] = useState(false);
  useEffect(() => {
    let timeout = null;
    const isStopPassed = isPassed(stop, trackerLayer.time, stations, idx);
    setIsStationPassed(isStopPassed);
    if (stop.state === "TIME_BASED" && !isStopPassed) {
      timeout = setInterval(() => {
        setIsStationPassed(isPassed(stop, trackerLayer.time, stations, idx));
      }, 2e4);
    }
    return () => {
      clearInterval(timeout);
    };
  }, [stop, trackerLayer, stations, idx]);
  return /* @__PURE__ */ React.createElement(
    "div",
    {
      role: "button",
      className: [
        "rt-route-station",
        isStationPassed ? " rt-passed" : "",
        isNotStation ? " rt-no-stop" : ""
      ].join(""),
      onClick: (e) => {
        return onStationClick(stop, e);
      },
      tabIndex: 0,
      onKeyPress: (e) => {
        return e.which === 13 && onStationClick(stop, e);
      }
    },
    /* @__PURE__ */ React.createElement("div", { className: "rt-route-delay" }, arrivalDelay === void 0 || arrivalDelay === null || isFirstStation || cancelled ? "" : /* @__PURE__ */ React.createElement(
      "span",
      {
        className: `rt-route-delay-arrival${` ${getDelayColor(
          arrivalDelay
        )}`}`
      },
      `+${getDelayString(arrivalDelay)}`
    ), departureDelay === void 0 || departureDelay === null || isLastStation || cancelled ? "" : /* @__PURE__ */ React.createElement(
      "span",
      {
        className: `rt-route-delay-departure${` ${getDelayColor(
          departureDelay
        )}`}`
      },
      `+${getDelayString(departureDelay)}`
    )),
    /* @__PURE__ */ React.createElement("div", { className: "rt-route-times" }, /* @__PURE__ */ React.createElement(
      "span",
      {
        className: `rt-route-time-arrival ${cancelled ? "rt-route-cancelled" : ""}`
      },
      getHoursAndMinutes(aimedArrivalTime)
    ), /* @__PURE__ */ React.createElement(
      "span",
      {
        className: `rt-route-time-departure ${cancelled ? "rt-route-cancelled" : ""}`
      },
      getHoursAndMinutes(aimedDepartureTime)
    )),
    renderStationImg(stations, idx, isStationPassed, isNotStation),
    renderStationName(stations, idx, cancelled)
  );
}
const defaultRenderStation = (props) => {
  const { stationId, arrivalTime, departureTime, stationName } = props.stop;
  return /* @__PURE__ */ React.createElement(
    RouteStop,
    {
      key: (stationId || stationName) + arrivalTime + departureTime,
      ...props
    }
  );
};
const defaultRenderRouteIdentifier = ({ routeIdentifier, longName }) => {
  if (routeIdentifier) {
    const id = parseInt(routeIdentifier.split(".")[0], 10);
    if (!longName.includes(id) && !Number.isNaN(id)) {
      return ` (${id})`;
    }
  }
  return null;
};
const defaultRenderHeader = ({
  lineInfos,
  renderHeaderButtons,
  renderRouteIdentifier
}) => {
  const {
    type,
    vehicleType,
    shortName,
    longName,
    stroke,
    destination,
    routeIdentifier,
    text_color: textColor
  } = lineInfos;
  return /* @__PURE__ */ React.createElement("div", { className: "rt-route-header" }, /* @__PURE__ */ React.createElement(
    "span",
    {
      className: "rt-route-icon",
      style: {
        /* stylelint-disable-next-line value-keyword-case */
        backgroundColor: stroke || getBgColor(type || vehicleType),
        color: textColor || "black"
      }
    },
    shortName
  ), /* @__PURE__ */ React.createElement("div", { className: "rt-route-title" }, /* @__PURE__ */ React.createElement("span", { className: "rt-route-name" }, destination), /* @__PURE__ */ React.createElement("span", null, longName, renderRouteIdentifier(lineInfos))), /* @__PURE__ */ React.createElement("div", { className: "rt-route-buttons" }, renderHeaderButtons(routeIdentifier)));
};
const defaultRenderFooter = (props) => {
  const { lineInfos, renderCopyright } = props;
  if (!lineInfos.operator && !lineInfos.publisher) {
    return null;
  }
  return /* @__PURE__ */ React.createElement("div", { className: "rt-route-footer" }, renderCopyright({ ...props }));
};
const defaultRenderLink = (text, url) => {
  return /* @__PURE__ */ React.createElement("div", { className: "rt-route-copyright-link" }, url ? /* @__PURE__ */ React.createElement("a", { href: url, target: "_blank", rel: "noreferrer" }, text) : text);
};
const defaultRenderCopyright = ({ lineInfos }) => {
  return /* @__PURE__ */ React.createElement("span", { className: "rt-route-copyright" }, lineInfos.operator && defaultRenderLink(lineInfos.operator, lineInfos.operatorUrl), lineInfos.operator && lineInfos.publisher && /* @__PURE__ */ React.createElement("span", null, "\xA0-\xA0"), lineInfos.publisher && defaultRenderLink(lineInfos.publisher, lineInfos.publisherUrl), lineInfos.license && /* @__PURE__ */ React.createElement("span", null, "\xA0("), lineInfos.license && defaultRenderLink(lineInfos.license, lineInfos.licenseUrl), lineInfos.license && ")");
};
const propTypes = {
  /**
   * CSS class of the route schedule wrapper.
   */
  className: PropTypes.string,
  /**
   * Trajectory stations informations.
   */
  lineInfos: ReactTransitPropTypes.lineInfos,
  /**
   * Trackerlayer.
   */
  trackerLayer: PropTypes.instanceOf(TrackerLayer).isRequired,
  /**
   * Render Header of the route scheduler.
   */
  renderHeader: PropTypes.func,
  /**
   * Render Footer of the route scheduler.
   */
  renderFooter: PropTypes.func,
  /**
   * Render Copyright of the route scheduler.
   */
  renderCopyright: PropTypes.func,
  /**
   * Render the route identifier in the header
   */
  renderRouteIdentifier: PropTypes.func,
  /**
   * Render the status of the station image.
   */
  renderStationImg: PropTypes.func,
  /**
   * Render a station.
   */
  renderStation: PropTypes.func,
  /**
   * Render a station name.
   */
  renderStationName: PropTypes.func,
  /**
   * Function triggered on station's click event.
   */
  onStationClick: PropTypes.func,
  /**
   * Function to render header buttons.
   */
  renderHeaderButtons: PropTypes.func
};
const defaultProps = {
  className: "rt-route-schedule",
  lineInfos: null,
  renderHeader: defaultRenderHeader,
  renderStation: defaultRenderStation,
  renderStationImg: defaultRenderStationImg,
  renderStationName: defaultRenderStationName,
  renderCopyright: defaultRenderCopyright,
  renderFooter: defaultRenderFooter,
  renderRouteIdentifier: defaultRenderRouteIdentifier,
  renderHeaderButtons: () => {
    return null;
  },
  onStationClick: () => {
  }
};
function RouteSchedule(props) {
  const { lineInfos, className, renderStation, renderHeader, renderFooter } = props;
  if (!lineInfos) {
    return null;
  }
  return /* @__PURE__ */ React.createElement("div", { className }, renderHeader({ ...props }), /* @__PURE__ */ React.createElement("div", { className: "rt-route-body" }, lineInfos.stations.map((stop, idx) => {
    return renderStation({ ...props, stop, idx });
  })), renderFooter({ ...props }));
}
RouteSchedule.propTypes = propTypes;
RouteSchedule.defaultProps = defaultProps;
export default React.memo(RouteSchedule);
